import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Divider,
  Group,
  List,
  Loader,
  Modal,
  Paper,
  RingProgress,
  ScrollArea,
  Stack,
  Text,
  Textarea,
} from '@mantine/core'
import { Dropzone } from '@mantine/dropzone'
import { notifications } from '@mantine/notifications'
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconDownload,
  IconFileSpreadsheet,
  IconUpload,
  IconX,
} from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { theme } from '@/configs/theme'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { BulkUploadJobStatus } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'

import { reshapeBulkUploadCsv } from './bulkUpload'

interface BulkUploadModalProps {
  opened: boolean
  onClose: () => void
}

const TERMINAL_STATUSES: ReadonlySet<BulkUploadJobStatus['status']> = new Set(['completed', 'failed'])

const isTerminal = (status?: BulkUploadJobStatus['status']) => !!status && TERMINAL_STATUSES.has(status)

export function BulkUploadModal({ opened, onClose }: BulkUploadModalProps) {
  const { api } = useGlobalState()
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [personalMessage, setPersonalMessage] = useState('')
  const [isPreparing, setIsPreparing] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)

  const handleClose = () => {
    setUploadedFile(null)
    setPersonalMessage('')
    setJobId(null)
    onClose()
  }

  const createBulkUsersMutation = useMutation({
    mutationFn: api.createBulkUsers,
    onSuccess: response => {
      setJobId(response.jobId)
    },
    onError: error => {
      const description = (error as any).response?.data?.detail ?? 'Failed to upload the CSV. Please try again.'
      notifications.show({
        title: 'Upload failed',
        message: description,
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  const jobQuery = useQuery({
    queryKey: ['users', 'bulkUpload', 'job', jobId],
    queryFn: () => api.getBulkUploadJobStatus({ jobId: jobId! }),
    enabled: !!jobId,
    refetchInterval: query => (isTerminal(query.state.data?.status) ? false : 1000),
  })

  const job = jobQuery.data
  const jobFinished = isTerminal(job?.status)

  // Members are created in the background, so refresh the list once the job finishes.
  useEffect(() => {
    if (job?.status === 'completed') {
      api.queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
      api.queryClient.invalidateQueries({ queryKey: ['billing', 'info'] })
    }
  }, [job?.status, api.queryClient])

  const handleDownloadTemplate = () => {
    const link = document.createElement('a')
    link.href = '/example-template.csv'
    link.download = 'example-template.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleUpload = async () => {
    if (!uploadedFile) return
    setIsPreparing(true)
    const result = await reshapeBulkUploadCsv(uploadedFile, personalMessage)
    setIsPreparing(false)
    if ('error' in result) {
      notifications.show({
        title: 'Check your CSV',
        message: result.error,
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      return
    }
    createBulkUsersMutation.mutate({ file: result.file })
  }

  const showProgressView = !!jobId

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      size={720}
      closeOnClickOutside={!showProgressView || jobFinished}
      title={
        <Group gap="xs" wrap="nowrap">
          {!showProgressView && (
            <ActionIcon variant="subtle" color="gray" onClick={handleClose} aria-label="Back">
              <IconArrowLeft size={20} />
            </ActionIcon>
          )}
          <Text fw={600} fz="lg">
            Upload Users via CSV
          </Text>
        </Group>
      }>
      {showProgressView ? (
        <BulkUploadProgress job={job} isError={jobQuery.isError} error={jobQuery.error} onDone={handleClose} />
      ) : (
        <Stack gap="lg">
          <Text size="sm" c="dimmed">
            Upload a CSV file to add End Users to your channel. Users are created in the background once the file is
            accepted. Use the template provided below.
          </Text>

          <Button
            variant="outline"
            leftSection={<IconDownload size={18} />}
            onClick={handleDownloadTemplate}
            w="fit-content">
            Download template
          </Button>

          <Paper withBorder radius="md" p="md" bg="gray.0">
            <Stack gap="xs">
              <Text fw={600} size="sm">
                CSV requirements
              </Text>
              <List size="sm" spacing={4} c="dimmed">
                <List.Item>Columns: name, email (required)</List.Item>
                <List.Item>The first row must be the header row</List.Item>
                <List.Item>Headers are case-insensitive and can be in any order</List.Item>
                <List.Item>Up to 500 users per upload</List.Item>
              </List>
            </Stack>
          </Paper>

          <Dropzone
            onDrop={files => files[0] && setUploadedFile(files[0])}
            accept={{ 'text/csv': ['.csv'] }}
            multiple={false}
            styles={_ => ({
              root: {
                borderColor: theme.colors.primaryColors[6],
                backgroundColor: theme.colors.primaryColors[0],
                borderStyle: 'dashed',
                '&:hover': {
                  backgroundColor: theme.colors.primaryColors[2],
                },
              },
            })}>
            <Stack gap="xs" align="center" justify="center" h={160}>
              {uploadedFile ? (
                <Group gap="xs" align="center">
                  <IconFileSpreadsheet size={32} stroke={1.5} color={theme.colors.primaryColors[6]} />
                  <Box>
                    <Text size="sm" fw={500}>
                      {uploadedFile.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {(uploadedFile.size / 1024).toFixed(1)} KB
                    </Text>
                  </Box>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label="Remove uploaded file"
                    style={{ pointerEvents: 'auto' }}
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      setUploadedFile(null)
                    }}>
                    <IconX size={18} aria-hidden="true" />
                  </ActionIcon>
                </Group>
              ) : (
                <>
                  <Dropzone.Accept>
                    <IconUpload size={32} stroke={1.5} color={theme.colors.primaryColors[6]} />
                  </Dropzone.Accept>
                  <Dropzone.Reject>
                    <IconUpload size={32} stroke={1.5} color={theme.colors.red[6]} />
                  </Dropzone.Reject>
                  <Dropzone.Idle>
                    <IconUpload size={32} stroke={1.5} color={theme.colors.primaryColors[6]} />
                  </Dropzone.Idle>
                  <Text ta="center" size="sm">
                    Drag and drop your CSV here, or click to browse
                  </Text>
                  <Text size="xs" c="dimmed" ta="center">
                    Supported format: CSV
                  </Text>
                </>
              )}
            </Stack>
          </Dropzone>

          <Textarea
            label="Personal message (optional)"
            description="Added to every invitation email in this upload."
            placeholder="Add a message to include in the invitation email..."
            rows={2}
            value={personalMessage}
            onChange={e => setPersonalMessage(e.currentTarget.value)}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              loading={isPreparing || createBulkUsersMutation.isPending}
              disabled={!uploadedFile}>
              Upload
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  )
}

interface BulkUploadProgressProps {
  job: BulkUploadJobStatus | undefined
  isError: boolean
  error: unknown
  onDone: () => void
}

function BulkUploadProgress({ job, isError, error, onDone }: BulkUploadProgressProps) {
  if (isError) {
    const description =
      (error as any)?.response?.data?.detail ??
      'We lost track of this upload. Users may still have been created — refresh the list to check.'
    return (
      <Stack gap="lg">
        <Alert variant="light" color="red" title="Couldn’t track this upload" icon={<IconAlertTriangle size={18} />}>
          {description}
        </Alert>
        <Group justify="flex-end">
          <Button onClick={onDone}>Close</Button>
        </Group>
      </Stack>
    )
  }

  // `total` reads 0 until the backend finishes parsing, so the stats aren't ready yet.
  if (!job || job.status === 'started' || (job.status === 'in_progress' && job.total === 0)) {
    return (
      <Stack gap="lg" align="center" py="xl">
        <Loader />
        <Text size="sm" c="dimmed">
          Preparing your upload…
        </Text>
      </Stack>
    )
  }

  if (job.status === 'failed') {
    return (
      <Stack gap="lg">
        <Alert variant="light" color="red" title="Upload failed" icon={<IconAlertTriangle size={18} />}>
          {job.error ?? 'The upload could not be processed. Please check your file and try again.'}
        </Alert>
        <Group justify="flex-end">
          <Button onClick={onDone}>Close</Button>
        </Group>
      </Stack>
    )
  }

  const isComplete = job.status === 'completed'
  const hasFailures = job.errorCount > 0
  const hiddenErrorCount = job.errorCount - job.errors.length

  const completedCount = Math.max(job.processed - job.errorCount, 0)
  const inProgressCount = Math.max(job.total - job.processed, 0)
  const percent = job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0

  const ringColor = isComplete ? (hasFailures ? 'yellow' : 'green') : theme.colors.primaryColors[6]

  const stats: { label: string; value: number; color?: string }[] = [
    { label: 'Total users', value: job.total },
    { label: 'Processed', value: job.processed },
    { label: 'In progress', value: inProgressCount },
    { label: 'Completed', value: completedCount, color: 'green' },
    { label: 'Failed', value: job.errorCount, color: job.errorCount > 0 ? 'red' : undefined },
  ]

  return (
    <Stack gap="lg">
      <Stack gap="xs" align="center">
        <RingProgress
          size={150}
          thickness={12}
          roundCaps
          sections={[{ value: percent, color: ringColor }]}
          label={
            <Text ta="center" fw={700} fz={28}>
              {percent}%
            </Text>
          }
        />
        <Text fw={600} fz="lg" ta="center">
          {isComplete ? 'Upload complete' : 'Processing your upload…'}
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          {isComplete
            ? `${completedCount} of ${job.total} ${job.total === 1 ? 'invitation' : 'invitations'} sent.`
            : 'Invitations are being sent. This may take a few minutes.'}
        </Text>
      </Stack>

      <Box>
        {stats.map((stat, i) => (
          <Box key={stat.label}>
            <Group justify="space-between" py="sm" px={4} wrap="nowrap">
              <Text size="sm" c="dimmed">
                {stat.label}
              </Text>
              <Text size="sm" fw={600} c={stat.color}>
                {stat.value}
              </Text>
            </Group>
            {i < stats.length - 1 && <Divider />}
          </Box>
        ))}
      </Box>

      {hasFailures && (
        <Alert
          variant="light"
          color="yellow"
          title={`${job.errorCount} ${job.errorCount === 1 ? 'row' : 'rows'} couldn’t be added`}
          icon={<IconAlertTriangle size={18} />}>
          <Stack gap="xs">
            <ScrollArea.Autosize mah={160}>
              <List size="sm" spacing={4}>
                {job.errors.map((message, i) => (
                  <List.Item key={i}>{message}</List.Item>
                ))}
              </List>
            </ScrollArea.Autosize>
            {hiddenErrorCount > 0 && (
              <Text size="xs" c="dimmed">
                …and {hiddenErrorCount} more not shown.
              </Text>
            )}
          </Stack>
        </Alert>
      )}

      <Group justify="flex-end" mt="md">
        <Button variant={isComplete ? 'filled' : 'outline'} onClick={onDone}>
          {isComplete ? 'Done' : 'Close'}
        </Button>
      </Group>

      {!isComplete && (
        <Text size="xs" c="dimmed" ta="center">
          You can close this window — the upload continues in the background.
        </Text>
      )}
    </Stack>
  )
}
